import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { chatAPI } from '../../services/api';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function ChatWithAI() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const result = await chatAPI.getHistory() as { success?: boolean; messages?: { role: string; content: string }[] };
      if (result?.success && result?.messages?.length) {
        setMessages(result.messages.map((m: any) => ({ role: m.role, content: m.content })).filter((m: { role: string }) => m.role !== 'system'));
      } else {
        setMessages([{ role: 'assistant', content: t('aiAssistant.welcomeMessage') }]);
      }
    } catch {
      setMessages([{ role: 'assistant', content: t('aiAssistant.welcomeMessage') }]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const result = await chatAPI.sendMessage(text) as { success?: boolean; response?: string; message?: string };
      if (result?.success && result?.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: result.response! }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: result?.message || t('aiAssistant.errors.generic') }]);
      }
    } catch (err: any) {
      setMessages(prev => [...prev, { role: 'assistant', content: err.message || t('aiAssistant.errors.generic') }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(t('aiAssistant.confirmClearHistory'))) return;
    await chatAPI.clearHistory();
    setMessages([{ role: 'assistant', content: t('aiAssistant.chatCleared') }]);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loadingHistory) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col" style={{ height: 'calc(100vh - 180px)' }}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('aiAssistant.title')}</h1>
        <button onClick={handleClear} className="text-sm text-red-500 hover:text-red-700">
          {t('aiAssistant.clearChat')}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-white rounded-xl shadow-sm p-4 space-y-3 mb-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-sm'
                : 'bg-gray-100 text-gray-800 rounded-bl-sm'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={t('aiAssistant.placeholder')}
          className="flex-1 resize-none border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          rows={1}
          disabled={loading}
        />
        <button
          onClick={handleSend}
          disabled={loading || !input.trim()}
          className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('aiAssistant.send')}
        </button>
      </div>
    </div>
  );
}
